/**
 * Hook + provider de autenticação. Reflete o estado do Firebase Auth + custom claims.
 * O resto do app NUNCA chama firebase/auth direto — só usa useAuth().
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  type User,
} from 'firebase/auth'
import { auth } from '@/config/firebase'
import { registrarAcesso } from '@/services/functions'
import type { SessionUser, Role, Permission } from '@/types'
import { getImpersonationTid } from '@/modules/admin/impersonation'

interface AuthState {
  user: SessionUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  refresh: () => Promise<void>
  hasPermission: (perm: Permission) => boolean
  /**
   * Inicia troca de e-mail. O Firebase manda um link de verificação pro NOVO
   * e-mail; só depois que o cliente clicar nesse link, o e-mail muda de fato.
   * Requer reautenticação com a senha atual (auth/requires-recent-login).
   * Lança erro com code conhecido (wrong-password, email-already-in-use, etc).
   */
  changeEmail: (newEmail: string, currentPassword: string) => Promise<void>
  /**
   * Troca a senha diretamente. Requer reauth com a senha atual.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

async function fbUserToSession(fb: User): Promise<SessionUser> {
  const token = await fb.getIdTokenResult()
  const claims = token.claims as Record<string, unknown>
  const role = ((claims.role as Role) || 'tenant_viewer')

  // Impersonation: só master pode. Se o localStorage tiver tenantId
  // de impersonate ativo, sobrescreve o tenantId real do master.
  let tenantId = (claims.tenantId as string) || null
  let impersonating: string | undefined = (claims.impersonating as string) || undefined

  if (role === 'master') {
    const impTid = getImpersonationTid()
    if (impTid) {
      tenantId = impTid
      impersonating = impTid
    }
  }

  return {
    uid: fb.uid,
    email: fb.email || '',
    name: fb.displayName || undefined,
    role,
    tenantId,
    permissions: (claims.permissions as Permission[]) || [],
    impersonating,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async fb => {
      if (!fb) {
        setUser(null)
      } else {
        const sessao = await fbUserToSession(fb)
        setUser(sessao)
        void registrarAcessoDaAba(sessao)
      }
      setLoading(false)
    })
    return unsub
  }, [])

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    // Marca ANTES de entrar: o onIdTokenChanged pode disparar antes do await
    // terminar, e é ele quem registra o acesso (como 'login', não 'abertura').
    sessionStorage.setItem(CHAVE_ACESSO_PENDENTE, 'login')
    try {
      await signInWithEmailAndPassword(auth, email, password)
      // user state will update via onIdTokenChanged
    } catch (e) {
      sessionStorage.removeItem(CHAVE_ACESSO_PENDENTE)
      throw e
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    // Libera o próximo login desta aba pra ser registrado de novo.
    limparAcessosDaAba()
    await fbSignOut(auth)
    setUser(null)
  }

  const resetPassword = (email: string) => sendPasswordResetEmail(auth, email)

  const changeEmail = async (newEmail: string, currentPassword: string) => {
    const fb = auth.currentUser
    if (!fb || !fb.email) throw new Error('Sessão expirada. Faça login de novo.')
    // Reauth com a senha atual — exigência de segurança do Firebase
    const cred = EmailAuthProvider.credential(fb.email, currentPassword)
    await reauthenticateWithCredential(fb, cred)
    // Manda link de verificação pro NOVO e-mail. Só depois que o cliente
    // clicar no link, o e-mail muda. Protege contra typo / e-mail que ela
    // não tem acesso de verdade.
    await verifyBeforeUpdateEmail(fb, newEmail.trim())
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const fb = auth.currentUser
    if (!fb || !fb.email) throw new Error('Sessão expirada. Faça login de novo.')
    if (newPassword.length < 6) throw new Error('A senha deve ter pelo menos 6 caracteres.')
    const cred = EmailAuthProvider.credential(fb.email, currentPassword)
    await reauthenticateWithCredential(fb, cred)
    await updatePassword(fb, newPassword)
  }

  const refresh = async () => {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true) // força refresh do token
      const sessao = await fbUserToSession(auth.currentUser)
      setUser(sessao)
      // Entrar/sair do modo suporte passa por aqui: registra a entrada no tenant.
      void registrarAcessoDaAba(sessao)
    }
  }

  const hasPermission = (perm: Permission): boolean => {
    if (!user) return false
    if (user.role === 'master') return true
    return user.permissions.includes(perm)
  }

  return (
    <AuthContext.Provider value={{
      user, loading, signIn, signOut, resetPassword, refresh, hasPermission,
      changeEmail, changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}

// ─── Histórico oculto de acessos (a partir de 01/01/2027) ───
// Registra 1× por aba: o onIdTokenChanged também dispara na renovação
// automática do token (a cada hora), e isso não é um acesso novo.
const CHAVE_ACESSO_PENDENTE = 'ep_acesso_pendente'
const PREFIXO_ACESSO_FEITO = 'ep_acesso_feito:'

async function registrarAcessoDaAba(sessao: SessionUser) {
  // Em modo suporte o master "entra" no tenant da cliente: conta à parte.
  const chave = `${PREFIXO_ACESSO_FEITO}${sessao.uid}|${sessao.impersonating || ''}`
  if (sessionStorage.getItem(chave)) return
  sessionStorage.setItem(chave, '1')
  const forma = sessionStorage.getItem(CHAVE_ACESSO_PENDENTE) === 'login' ? 'login' : 'abertura'
  sessionStorage.removeItem(CHAVE_ACESSO_PENDENTE)
  try {
    await registrarAcesso({ forma, suporteTenantId: sessao.impersonating || null })
  } catch {
    // Best-effort: falha no histórico nunca impede a pessoa de usar o sistema.
  }
}

function limparAcessosDaAba() {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const k = sessionStorage.key(i)
    if (k?.startsWith(PREFIXO_ACESSO_FEITO)) sessionStorage.removeItem(k)
  }
}
